"use client";

import type { TextColor } from "@homeinstead/schemas";
import type { FranchiseInquiryFormInput } from "./franchiseInquiry.schema";

import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import { useFormContext } from "react-hook-form";
import {
  InquiryStepHeading,
  InquiryTextareaField,
  InquiryTextField,
  inquiryFormErrorTextClass,
  useInquiryFieldError,
} from "~www/components/LeadForm/Inquiry";
import { FranchiseInquiryCountySelect } from "./FranchiseInquiryCountySelect";

type FranchiseInquiryInfoStepProps = {
  nextStep: () => void;
  textColor?: TextColor;
};

function FranchiseInquiryInfoStep({
  nextStep,
  textColor,
}: FranchiseInquiryInfoStepProps) {
  const { control, register, trigger } =
    useFormContext<FranchiseInquiryFormInput>();
  const { errors, setAttemptedStepContinue, shouldShowError } =
    useInquiryFieldError<FranchiseInquiryFormInput>();
  const errorColorClass = inquiryFormErrorTextClass(textColor);

  async function handleContinue() {
    setAttemptedStepContinue(true);
    const isValid = await trigger(["county", "postcode"]);
    if (isValid) {
      nextStep();
    }
  }

  return (
    <div className="flex w-full max-w-[440px] flex-col gap-6">
      <InquiryStepHeading textColor={textColor}>
        Tell us about the franchise you are interested in
      </InquiryStepHeading>

      <div className="flex flex-col gap-6">
        <FranchiseInquiryCountySelect
          control={control}
          errorClassName={cn("mt-2", errorColorClass)}
          errorMessage={errors.county?.message}
          label="What county are you interested in?"
          showError={shouldShowError("county")}
          textColor={textColor}
        />

        <InquiryTextField
          autoComplete="postal-code"
          error={errors.postcode}
          errorClassName={errorColorClass}
          label="What is your postcode?"
          placeholder="Eircode"
          showError={shouldShowError("postcode")}
          textColor={textColor}
          type="text"
          {...register("postcode")}
        />

        <InquiryTextareaField
          error={errors.message}
          errorClassName={errorColorClass}
          label="Message (optional)"
          placeholder="Add any additional notes about the franchise you are interested in"
          showError={shouldShowError("message")}
          textColor={textColor}
          {...register("message")}
        />
      </div>

      <Button
        className="mt-2 h-12 w-full rounded-full"
        onClick={() => void handleContinue()}
        type="button"
        variant="friendly"
      >
        Continue
      </Button>
    </div>
  );
}

export { FranchiseInquiryInfoStep };
